import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GenericSelect } from '@/styles/GenericForm';
import { GenericTag } from '@/styles/GenericTag';
import { CancelButton, SaveChangesButton, StandardButton } from '@/styles/StandardButtons';
import { fetchTeamMemberLookup } from '@/lib/entity-api';
import { fetchAllItems, fetchCostCodes, saveItem, saveOffer } from '../services/catalog-api';
import {
  IMPORT_FIELDS,
  MAX_IMPORT_ROWS,
  autoMapColumns,
  buildPreview,
  countByStatus,
  parseCsvText,
  type ImportContext,
  type ImportField,
  type ParsedCsv,
  type PreviewRow,
} from '../lib/csv-import';

interface ImportItemsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** items were created (the list reloads) */
  onImported: () => void;
}

const NOT_IN_FILE = '__none__';
const PREVIEW_LIMIT = 100;

/**
 * Import items from a CSV: choose the file, map its columns, review every row (new / already in the catalog / error), import the new
 * ones. An item that already exists is skipped -- never updated -- so an import can neither duplicate an item nor overwrite a price.
 * Prices are imported only together with their vendor, and only for the items the import creates.
 */
export function ImportItemsDialog({ isOpen, onClose, onImported }: ImportItemsDialogProps) {
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [context, setContext] = useState<ImportContext | null>(null);
  const [mapping, setMapping] = useState<Record<ImportField, number> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; failed: string[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => { setParsed(null); setContext(null); setMapping(null); setError(null); setResult(null); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const chooseFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const csv = parseCsvText(await file.text());
      if (csv.headers.length === 0 || csv.rows.length === 0) throw new Error('The file has no rows');
      const [existingItems, costCodes, vendors] = await Promise.all([fetchAllItems(), fetchCostCodes(), fetchTeamMemberLookup(['vendor'])]);
      setContext({
        existingItems: existingItems.map((item) => ({ name: item.name, sku: item.sku, costCodeId: item.cost_code_id, unit: item.unit })),
        costCodes: costCodes.filter((c) => !!c.parent_id).map((c) => ({ id: c.id, code: c.code })),
        vendors: vendors.map((v) => ({ id: v.id, name: v.company_name || v.full_name || `${v.first_name || ''} ${v.last_name || ''}`.trim() || v.email })),
      });
      setParsed(csv);
      setMapping(autoMapColumns(csv.headers));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not read the file');
    } finally {
      setBusy(false);
    }
  };

  const preview: PreviewRow[] = parsed && mapping && context && mapping.name >= 0 ? buildPreview(parsed.rows, mapping, context) : [];
  const counts = countByStatus(preview);

  const runImport = async () => {
    setBusy(true);
    setError(null);
    let created = 0;
    const failed: string[] = [];
    for (const row of preview.filter((r) => r.status === 'new')) {
      try {
        const item = await saveItem(null, { name: row.name, sku: row.sku, description: row.description, unit: row.unit, cost_code_id: row.costCodeId, is_active: true });
        created += 1;
        if (row.vendorId && row.unitPrice !== null) {
          try {
            await saveOffer(null, { item_id: item.id, vendor_id: row.vendorId, unit_price: row.unitPrice });
          } catch (failure) {
            failed.push(`Line ${row.line}: the item was created but its price was not (${failure instanceof Error ? failure.message : 'error'})`);
          }
        }
      } catch (failure) {
        failed.push(`Line ${row.line}: ${failure instanceof Error ? failure.message : 'error'}`);
      }
    }
    setResult({ created, failed });
    setBusy(false);
    if (created > 0) onImported();
  };

  const fieldOptions = parsed ? [{ label: 'Not in the file', value: NOT_IN_FILE }, ...parsed.headers.map((header, index) => ({ label: header || `Column ${index + 1}`, value: String(index) }))] : [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import items</DialogTitle>
          <DialogDescription>CSV file. Items already in the catalog are skipped: nothing is duplicated and no price is overwritten.</DialogDescription>
        </DialogHeader>

        {error && <div className="generic-form-error" role="alert">{error}</div>}

        {result ? (
          <div className="space-y-3" data-testid="import-result">
            <p className="text-sm">{result.created} item{result.created === 1 ? '' : 's'} created.</p>
            {result.failed.length > 0 && (
              <ul className="text-sm text-red-700 list-disc pl-5">{result.failed.map((message) => <li key={message}>{message}</li>)}</ul>
            )}
            <div className="flex justify-end"><SaveChangesButton type="button" onClick={close}>Done</SaveChangesButton></div>
          </div>
        ) : !parsed || !mapping ? (
          <div className="space-y-3">
            <input ref={fileRef} type="file" accept=".csv,text/csv" aria-label="CSV file" className="hidden" onChange={(event) => void chooseFile(event.target.files?.[0])} />
            <StandardButton type="button" customVariant="white" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? 'Reading...' : 'Choose CSV file'}</StandardButton>
            <p className="text-xs text-gray-500">Provisional import. Columns it understands: item name (required), SKU, description, cost code (the code, e.g. 1000), unit, vendor and unit price (a price needs its vendor). Up to {MAX_IMPORT_ROWS} rows.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {IMPORT_FIELDS.map(({ field, label, required }) => (
                <div key={field} className="space-y-1">
                  <label className="font-form-label text-sm" htmlFor={`map-${field}`}>{label}{required ? ' *' : ''}</label>
                  <GenericSelect
                    id={`map-${field}`}
                    value={mapping[field] >= 0 ? String(mapping[field]) : NOT_IN_FILE}
                    onValueChange={(value) => setMapping({ ...mapping, [field]: value === NOT_IN_FILE ? -1 : Number(value) })}
                    options={fieldOptions}
                  />
                </div>
              ))}
            </div>
            {parsed.truncated && <p className="text-sm text-amber-700">Only the first {MAX_IMPORT_ROWS} rows are imported.</p>}

            {mapping.name < 0 ? (
              <p className="text-sm text-red-700">Choose the column that holds the item name.</p>
            ) : (
              <>
                <div className="flex gap-4 text-sm" data-testid="import-counts">
                  <span><strong>{counts.new}</strong> new</span>
                  <span><strong>{counts.duplicate}</strong> already in the catalog / repeated (skipped)</span>
                  <span><strong>{counts.error}</strong> with errors (skipped)</span>
                </div>
                <div className="max-h-72 overflow-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="text-left text-gray-500">
                        <th className="p-2 font-medium">Line</th><th className="p-2 font-medium">Item</th><th className="p-2 font-medium">SKU</th><th className="p-2 font-medium">Cost code</th><th className="p-2 font-medium">Vendor / price</th><th className="p-2 font-medium">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.slice(0, PREVIEW_LIMIT).map((row) => (
                        <tr key={row.line} className="border-t border-gray-100" data-testid={`import-row-${row.line}`}>
                          <td className="p-2">{row.line}</td>
                          <td className="p-2">{row.name || '-'}</td>
                          <td className="p-2">{row.sku || '-'}</td>
                          <td className="p-2">{row.costCodeText || '-'}</td>
                          <td className="p-2">{row.vendorText ? `${row.vendorText} · ${row.unitPrice ?? ''}` : '-'}</td>
                          <td className="p-2">
                            <GenericTag type="status" value={row.status === 'new' ? 'approved' : row.status === 'duplicate' ? 'pending' : 'rejected'}>{row.status === 'new' ? 'New' : row.status === 'duplicate' ? 'Skipped' : 'Error'}</GenericTag>
                            {row.message && <span className="text-xs text-gray-500 ml-2">{row.message}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.length > PREVIEW_LIMIT && <p className="text-xs text-gray-500">Showing the first {PREVIEW_LIMIT} of {preview.length} rows.</p>}
              </>
            )}

            <div className="flex justify-end gap-2">
              <CancelButton onClick={close}>Cancel</CancelButton>
              <SaveChangesButton type="button" disabled={busy || counts.new === 0} onClick={() => void runImport()}>
                {busy ? 'Importing...' : `Import ${counts.new} item${counts.new === 1 ? '' : 's'}`}
              </SaveChangesButton>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
